import type { ClientKind } from "@/domain/clients/list-types";

const CLIENT_HANDLE_KINDS = {
  ind: (row: ClientBookRow) => (row.kind === "individual" ? row.id : null),
  client: (row: ClientBookRow) => row.icId,
} as const;

type ClientHandleKind = keyof typeof CLIENT_HANDLE_KINDS;

export interface ClientBookRow {
  kind: ClientKind;
  id: string;
  icId: string | null;
  partyId: string;
  name: string;
}

export interface ClientBook {
  live: readonly ClientBookRow[] | null;
  endedIndividualIds: readonly string[];
  endedIcIds: readonly string[];
}

export type ClientIdResolution =
  | { ok: true; row: ClientBookRow; message: string }
  | { ok: false; message: string };

async function clientHandle(kind: ClientHandleKind, rawId: string): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;
  const digest = await subtle.digest("SHA-256", new TextEncoder().encode(rawId));
  const hex = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
  return `${kind}-${hex.slice(0, DIGEST_CHARS)}`;
}

export async function resolveClientId(
  clientId: string,
  book: ClientBook,
): Promise<ClientIdResolution> {
  const id = clientId.trim();

  if (book.live === null) {
    return {
      ok: false,
      message:
        "The advisor's client book has not loaded in this browser yet, so " +
        "that id could not be checked and nothing was opened. This is not a " +
        "statement about whether the client exists. It can be tried once more " +
        "in a moment.",
    };
  }

  const exact = book.live.find(
    (row) => row.id === id || row.icId === id || (row.kind === "corporation" && row.partyId === id),
  );
  if (exact) return opened(exact);

  if (book.endedIndividualIds.includes(id) || book.endedIcIds.includes(id)) {
    return ended();
  }

  const named = book.live.filter((row) => sameName(row.name, id));
  if (named.length === 1) return opened(named[0]);
  if (named.length > 1) {
    return {
      ok: false,
      message:
        `More than one client in this advisor's book is named "${id}", so ` +
        "nothing was opened — choosing one would be a guess. Ask the advisor " +
        "which one they mean, or look the client up and pass the id from that " +
        "result.",
    };
  }

  const shaped = HANDLE_SHAPE.exec(id);
  if (shaped) {
    const [, kind] = shaped;
    if (!(kind in CLIENT_HANDLE_KINDS)) {
      return {
        ok: false,
        message:
          `"${id}" is a "${kind}" reference, not a reference to a client, so ` +
          'nothing was opened. Pass the client\'s own id, or its "client" or ' +
          '"ind" reference.',
      };
    }
    const readField = CLIENT_HANDLE_KINDS[kind as ClientHandleKind];

    const matches = await digestMatches(id, kind as ClientHandleKind, book.live, readField);
    if (matches === null) {
      return {
        ok: false,
        message:
          `This browser could not read the reference "${id}", so it was not ` +
          "checked against the advisor's book and nothing was opened. This is " +
          "not a statement about whether the client exists. Pass the client's " +
          "full id instead.",
      };
    }
    if (matches.length === 1) return opened(matches[0]);
    if (matches.length > 1) {
      return {
        ok: false,
        message:
          `More than one client in this advisor's book matches "${id}", so ` +
          "nothing was opened — choosing one would be a guess. Pass the " +
          "client's full id instead.",
      };
    }

    const endedRaw = kind === "ind" ? book.endedIndividualIds : book.endedIcIds;
    for (const raw of endedRaw) {
      if ((await clientHandle(kind as ClientHandleKind, raw)) === id) return ended();
    }
  }

  return {
    ok: false,
    message:
      "No client with that id or full name is in this advisor's book, so " +
      "nothing was opened — the view is unchanged. Do not report the client " +
      "as shown. Look the client up first and pass the id from that result.",
  };
}

function opened(row: ClientBookRow): ClientIdResolution {
  return {
    ok: true,
    row,
    message:
      `The client profile pane is now open on ${row.name}. That reports the ` +
      "view was changed, not that the pane has finished loading their figures.",
  };
}

function ended(): ClientIdResolution {
  return {
    ok: false,
    message:
      "That client's relationship with this advisor has ended, so they are " +
      "no longer in the advisor's book and the pane was not opened. Say so " +
      "rather than retrying.",
  };
}

function sameName(name: string, asked: string): boolean {
  const wanted = comparableName(asked);
  return wanted !== "" && comparableName(name) === wanted;
}

function comparableName(name: string): string {
  return name.trim().replace(/\s+/g, " ").toLocaleLowerCase("sv");
}

async function digestMatches(
  handle: string,
  kind: ClientHandleKind,
  live: readonly ClientBookRow[],
  readField: (row: ClientBookRow) => string | null,
): Promise<ClientBookRow[] | null> {
  const matched: ClientBookRow[] = [];
  for (const row of live) {
    const raw = readField(row);
    if (raw === null || raw === "") continue;
    const candidate = await clientHandle(kind, raw);
    if (candidate === null) return null;
    if (candidate === handle) matched.push(row);
  }
  return matched;
}

const HANDLE_SHAPE = /^([a-z]+)-([0-9a-f]{8})$/;

const DIGEST_CHARS = 8;
